import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ── shared styles ───────────────────────────────────────── */
const th = {
  padding: '0.5rem 0.75rem',
  background: 'linear-gradient(135deg,#D84315,#F57C00)',
  color: '#fff', fontWeight: 600, fontSize: '0.8rem',
  textAlign: 'left', whiteSpace: 'nowrap',
};
const td = {
  padding: '0.4rem 0.75rem',
  fontSize: '0.85rem', borderBottom: '1px solid #e0e0e0', color: '#2c3e50',
};
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

/* ── period definitions (computed once at module load) ───── */
const pad = (n) => String(n).padStart(2, '0');
const fmtD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };

const base = new Date();
base.setHours(0, 0, 0, 0);
const today = fmtD(base);

const PERIODS = [
  { label: 'Today',    from: today,              to: today },
  { label: 'Tomorrow', from: fmtD(addDays(base, 1)), to: fmtD(addDays(base, 1)) },
  { label: '2 Days',   from: today,              to: fmtD(addDays(base, 1)) },
  { label: '3 Days',   from: today,              to: fmtD(addDays(base, 2)) },
  { label: '4 Days',   from: today,              to: fmtD(addDays(base, 3)) },
  { label: '5 Days',   from: today,              to: fmtD(addDays(base, 4)) },
  { label: '6 Days',   from: today,              to: fmtD(addDays(base, 5)) },
  { label: 'Week',     from: today,              to: fmtD(addDays(base, 6)) },
  { label: '2 Weeks',  from: today,              to: fmtD(addDays(base, 13)) },
  { label: 'Month',    from: today,              to: fmtD(addDays(base, 29)) },
];

const fmt3 = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }));

/* ── collapsible section ─────────────────────────────────── */
function Section({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: '1.25rem', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.65rem 1rem', background: '#f8f9fa', cursor: 'pointer',
          borderBottom: open ? '1px solid #e0e0e0' : 'none',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
          {title}
          {count != null && (
            <span style={{ marginLeft: 8, background: '#e8eaed', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem', color: '#555' }}>
              {count}
            </span>
          )}
        </span>
        <span style={{ fontSize: '0.85rem', color: '#888' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ overflowX: 'auto' }}>{children}</div>}
    </div>
  );
}

/* ── requirements table ──────────────────────────────────── */
function ReqTable({ rows, nameKey, emptyMsg }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: '1rem', color: '#999', fontSize: '0.85rem', textAlign: 'center' }}>
        {emptyMsg || 'No data.'}
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Name</th>
          <th style={th}>UOM</th>
          <th style={{ ...th, textAlign: 'right' }}>Orders</th>
          <th style={{ ...th, textAlign: 'right' }}>On Hand</th>
          <th style={{ ...th, textAlign: 'right' }}>Requirement</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const diff = Number(r.difference);
          const requirement = diff < 0 ? -diff : 0;
          const reqColor = requirement > 0 ? '#c0392b' : '#27ae60';
          return (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={td}>{r[nameKey]}</td>
              <td style={{ ...td, color: '#666' }}>{r.uom || '—'}</td>
              <td style={tdNum}>{fmt3(r.qty_required)}</td>
              <td style={tdNum}>{fmt3(r.qty_on_hand)}</td>
              <td style={{ ...tdNum, color: reqColor, fontWeight: requirement > 0 ? 700 : 400 }}>
                {fmt3(requirement)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const inp = { padding: '0.35rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.85rem' };

/* ── simple stock table ─────────────────────────────────── */
function StockTable({ rows, nameKey, emptyMsg }) {
  if (!rows || rows.length === 0)
    return <div style={{ padding: '1rem', color: '#999', fontSize: '0.85rem', textAlign: 'center' }}>{emptyMsg || 'No data.'}</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Name</th>
          <th style={th}>UOM</th>
          <th style={{ ...th, textAlign: 'right' }}>On Hand</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
            <td style={td}>{r[nameKey]}</td>
            <td style={{ ...td, color: '#666' }}>{r.uom || '—'}</td>
            <td style={tdNum}>{fmt3(r.qty_on_hand)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RequirementsReport() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${user?.token}` };

  const [activeTab, setActiveTab]   = useState('requirements');
  const [periodIdx, setPeriodIdx]   = useState(0);
  const [locationId, setLocationId] = useState('2');
  const [locations, setLocations]   = useState([]);
  const [data, setData]             = useState(null);
  const [stockData, setStockData]     = useState(null);
  const [stockSearch, setStockSearch] = useState('');
  const [loading, setLoading]         = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/stock/locations`, { headers, params: { type: 'WAREHOUSE', active: 'true' } })
      .then(r => setLocations(r.data || [])).catch(() => {});
    fetchReport(0, '2');
    fetchStock('2');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReport = async (idx, locId) => {
    setLoading(true); setError('');
    const p = PERIODS[idx];
    const params = { from_date: p.from, to_date: p.to };
    if (locId) params.location_id = locId;
    try {
      const r = await axios.get(`${API_URL}/reports/item-requirements`, { headers, params });
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async (locId) => {
    if (!locId) { setStockData(null); return; }
    setStockLoading(true);
    try {
      const r = await axios.get(`${API_URL}/reports/current-stock`, { headers, params: { location_id: locId } });
      setStockData(r.data);
    } catch {
      setStockData(null);
    } finally {
      setStockLoading(false);
    }
  };

  const handlePeriod = (idx) => {
    setPeriodIdx(idx);
    fetchReport(idx, locationId);
  };

  const handleLocation = (val) => {
    setLocationId(val);
    fetchReport(periodIdx, val);
    fetchStock(val);
  };

  const period = PERIODS[periodIdx];

  const tabStyle = (active) => ({
    padding: '0.5rem 1.25rem', border: 'none', borderRadius: '6px 6px 0 0',
    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    background: active ? '#fff' : '#e8eaed',
    color: active ? '#D84315' : '#555',
    borderBottom: active ? '2px solid #D84315' : '2px solid transparent',
  });

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100 }}>
      <h2 style={{ marginBottom: '0.5rem', fontSize: '1.2rem', color: '#2c3e50' }}>
        Item / Product Requirements
      </h2>

      {/* ── Location filter ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Stock Location</span>
        <select style={inp} value={locationId} onChange={e => handleLocation(e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
        </select>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e0e0e0', marginBottom: '1.25rem' }}>
        <button style={tabStyle(activeTab === 'requirements')} onClick={() => setActiveTab('requirements')}>Requirements</button>
        <button style={tabStyle(activeTab === 'stock')} onClick={() => setActiveTab('stock')}>Current Stock</button>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c33', border: '1px solid #fcc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* ── Requirements tab ── */}
      {activeTab === 'requirements' && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
            {PERIODS.map((p, i) => (
              <button
                key={p.label}
                onClick={() => handlePeriod(i)}
                style={{
                  padding: '0.35rem 0.85rem', border: 'none', borderRadius: 20,
                  fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                  background: i === periodIdx ? 'linear-gradient(135deg,#D84315,#F57C00)' : '#e8eaed',
                  color: i === periodIdx ? '#fff' : '#444',
                  transition: 'background 0.15s',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '1rem', fontSize: '0.82rem', color: '#666' }}>
            {period.from === period.to
              ? `Showing orders for: ${period.from}`
              : `Showing orders from ${period.from} to ${period.to}`}
          </div>

          {loading && <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>Loading…</div>}

          {data && !loading && (
            <>
              <Section title="Products" count={data.products.length}>
                <ReqTable rows={data.products} nameKey="product_name" emptyMsg="No products ordered in this period." />
              </Section>
              <Section title="Child Products (from Combos)" count={data.child_products.length} defaultOpen={data.child_products.length > 0}>
                <ReqTable rows={data.child_products} nameKey="product_name" emptyMsg="No child products in this period." />
              </Section>
              <Section title="Raw Items (Ingredients)" count={data.items.length} defaultOpen={data.items.length > 0}>
                <ReqTable rows={data.items} nameKey="item_name" emptyMsg="No ingredient mappings found for ordered products." />
              </Section>
            </>
          )}
        </>
      )}

      {/* ── Current Stock tab ── */}
      {activeTab === 'stock' && (
        <>
          {!locationId && (
            <div style={{ color: '#888', fontSize: '0.85rem' }}>Select a location to view current stock.</div>
          )}
          {stockLoading && <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>Loading…</div>}
          {stockData && !stockLoading && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  style={{ ...inp, minWidth: 220 }}
                  placeholder="Search name..."
                  value={stockSearch}
                  onChange={e => setStockSearch(e.target.value)}
                />
                <button
                  style={{ padding: '0.35rem 0.9rem', border: 'none', borderRadius: 5, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', background: '#e0e0e0', color: '#333' }}
                  onClick={() => setStockSearch('')}
                >Reset</button>
              </div>
              {(() => {
                const q = stockSearch.toLowerCase();
                const filteredProducts = stockData.products.filter(r => !q || r.name?.toLowerCase().includes(q));
                const filteredItems    = stockData.items.filter(r => !q || r.name?.toLowerCase().includes(q));
                return (
                  <>
                    <Section title="Products" count={filteredProducts.length}>
                      <StockTable rows={filteredProducts} nameKey="name" emptyMsg="No products tagged to this location." />
                    </Section>
                    <Section title="Raw Items" count={filteredItems.length} defaultOpen={filteredItems.length > 0}>
                      <StockTable rows={filteredItems} nameKey="name" emptyMsg="No items tagged to this location." />
                    </Section>
                  </>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}
